import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import {NgForm} from "@angular/forms";

import L from "leaflet";
import {BsModalService} from "ngx-bootstrap/modal";
import {Subscription} from "rxjs";
import {Md5} from "ts-md5";
import {AppState} from "../../../core";
import {
    MapImageOverlay,
    MapImageOverlayBounds,
    MapImageOverlayDimension,
    MapImageOverlayPoint,
    MapRouteGPXOverlay,
} from "../../../core/models";
import {PathTestDoc} from "../../../core/schema/path-test-schema";
import {
    ScoutPointPhotoService,
    ScoutPointPhotoUploadService,
    UploadPhotoState,
    UploadProgressSubject,
} from "../../../core/services";
import {ConfirmModalComponent} from "../../components/confirm-modal/confirm-modal.component";
import {FesBaseComponent} from "../../components/fes-base.component";

@Component({
    template: "",
})
export class MapImageOverlayComponent
    extends FesBaseComponent
    implements OnInit, OnDestroy {
    @Input() pathTestDoc: PathTestDoc;
    @Input() overlay: MapImageOverlay = new MapImageOverlay();
    @Input() gpx: MapRouteGPXOverlay = new MapRouteGPXOverlay();
    @Input() headerTitle = 'Overlay';
    @Input() readonly = false;
    @Input() fallbackOverlayCopyTitle = 'Copy/Save'
    @Input() fallbackOverlay : MapImageOverlay;  // a default to copy from if overlay is empty

    @Output() overlayEdited = new EventEmitter<MapImageOverlay>();

    @Output() gpxEdited = new EventEmitter<MapRouteGPXOverlay>();

    @ViewChild("overlayForm") overlayForm: NgForm;

    // need id to use for photo id (i.e. scout path id or test path id)
    getOwnerId(): string {
        throw new Error("getOwnerId() not implemented");
    }

    // scout paths can share overlays but paths current cannot
    getSharableOverlays(): {
        ownerId: string;
        ownerIndex: number;
        overlay: MapImageOverlay;
    }[] {
        throw new Error("getSharableOverlays() not implemented");
    }

    // scout paths and test paths define how to save
    saveOverlay(overlay: MapImageOverlay): Promise<string> {
        throw new Error("saveOverlay() not implemented");
    }

    saveRouteOverlay(overlay: MapRouteGPXOverlay): Promise<string> {
        throw new Error("saveRouteOverlay() not implemented");
    }

    bProcessing = false;
    bProcessingforRoutesoverlay = false;
    errorMsg = "";
    bEditing = false;
    //Editing for routes overlay
    bEditingForRoutes = false;
    uploadStartedSubscription: Subscription;
    imageUploadProgress = 0;
    imageFile: File;
    gpxFile: File;
    bImageUploadProcessing = false;
    bWorldDataProcessing = false;
    bRouteFileProcessing = false;
    imageFileName: string;
    worldDataFileName: string;
    routesGpxFileName: string;
    existingOverlays: {
        ownerId: string;
        ownerIndex: number;
        overlay: MapImageOverlay;
    }[] = [];

    constructor(
        appState: AppState,
        protected photoService: ScoutPointPhotoService,
        protected photoUploadService: ScoutPointPhotoUploadService,
        protected modalService: BsModalService
    ) {
        super(appState);
    }

    ngOnInit(): void {
        this.initFormValues();
        this.subscribe(
            this.photoUploadService.uploadStartedEmitter,
            (progressSubject: UploadProgressSubject) => {
                if (progressSubject.photoId == this.getOwnerId()) {
                    this.addUploadProgressSubscription(progressSubject);
                }
            }
        );
    }

    ngOnDestroy(): void {
        this.destroy();
        this.uploadStartedSubscription?.unsubscribe();
    }

    addUploadProgressSubscription(progressEmitter: UploadProgressSubject) {
        this.uploadStartedSubscription?.unsubscribe();
        this.imageUploadProgress = progressEmitter?.getValue()?.percentComplete;
        this.uploadStartedSubscription = progressEmitter.subscribe(
            (uploadState: UploadPhotoState) =>
                (this.imageUploadProgress = uploadState.percentComplete),
            (error) => (this.imageUploadProgress = 0),
            () => (this.imageUploadProgress = 0)
        );
    }

    async askDiscardEdits(acceptDiscardFn: () => void) {
        let initialState = {
            onYes: acceptDiscardFn,
            question: "Discard unsaved scout path overlay?",
            focusOnNo: true,
            yesStyle: "btn btn-danger",
        };
        this.modalService.config.animated = false;
        this.modalService.show(ConfirmModalComponent, {initialState});
    }

    canSave() {
        return (
            !this.bProcessing &&
            this.isModified() &&
            this.overlay?.imageDataUrl &&
            this.overlay.bounds
        );
    }

    // Function to check If we can save the choosen file or not

    canSaveRoutesgpx() {
        return !this.bProcessingforRoutesoverlay && this.gpx?.gpxDataUrl;
    }

    initFormValues() {
        this.existingOverlays = this.getSharableOverlays();
    }

    isModified = () => {
        return this.overlayForm?.dirty;
    };

    resetEditableOverlay() {
        const sourceOverlay = this.overlay;
        if (sourceOverlay) {
            this.overlay = JSON.parse(JSON.stringify(sourceOverlay));
        } else {
            this.overlay = null;
        }
    }

    resetEditableRouteOverlay() {
        const sourceOverlay = this.gpx;
        if (sourceOverlay) {
            this.gpx = JSON.parse(JSON.stringify(sourceOverlay));
        } else {
            this.gpx = null;
        }
    }

    onCancel(): void {
        this.imageFile = null;
        this.resetEditableOverlay();

        this.bEditing = false;

        this.overlayForm.reset();
        this.overlayEdited.emit(this.overlay);
    }

    onCancelroutegpxfile(from_save=false): void {
        if (!from_save) {
            this.gpx = this.pathTestDoc.gpx;
        }
        this.resetEditableRouteOverlay();

        // For editing overlay routes(gpx)

        this.bEditingForRoutes = false;
        this.gpxEdited.emit(this.gpx);

    }

    async copyOverlay(srcOverlayInfo: {
        ownerIndex: number;
        overlay: MapImageOverlay;
    }) {
        this.overlay = srcOverlayInfo.overlay;
        this.overlay.imageDataUrl = "hack-so-save-button-enabled";
        this.overlayForm.form.markAsDirty();
    }

    async onDelete() {
        try {
            let oldImageId = await this.saveOverlay(null);
            await this.deleteImage(oldImageId);
            this.onCancel();
        } catch (error) {
            console.error(error);
        }
    }

    async onDeleteRouteOverlay() {
        try {
            await this.deleteRouteFile(this.gpx.routeId)
            this.onCancelroutegpxfile();
            this.overlayForm.reset();
            this.overlayEdited.emit(this.overlay);
        } catch (error) {
            console.error(error);
        }
    }

    onEdit() {
        this.resetEditableOverlay();
        this.bEditing = true;
    }

    async saveFromFallback() {
        let overlay = new MapImageOverlay();
            overlay.bounds = this.fallbackOverlay.bounds;
            overlay.imageId = this.fallbackOverlay.imageId;
            overlay.imageSize = this.fallbackOverlay.imageSize;
            overlay.jpgName = this.fallbackOverlay.jpgName;
            overlay.jgwName = this.fallbackOverlay.jgwName;
            overlay.pixelSize = this.fallbackOverlay.pixelSize;
            overlay.topLeft = this.fallbackOverlay.topLeft;
        return await this.saveOverlay(overlay);
    }

    //Routes_overlay (Gpx)

    onEditroutesoverlay() {
        this.resetEditableRouteOverlay();
        this.bEditingForRoutes = true;
    }

    async onImageFileInputChanged(event) {
        this.bImageUploadProcessing = true;
        if (!this.overlay) {
            this.overlay = new MapImageOverlay();
        }
        try {
            this.errorMsg = "";
            this.imageFile = (event.target as HTMLInputElement).files[0];
            event.srcElement.value = null; // detect diffent file content but same filename on next edit
            var reader = new FileReader();
            const fileLoadPromise = new Promise<void>((completeFn, errorFn) => {
                reader.onload = async (event) => {
                    this.overlay.imageDataUrl = reader.result as string;
                    completeFn();
                };
                reader.onerror = (error) => {
                    errorFn(error);
                };
            });
            reader.readAsDataURL(this.imageFile);
            await fileLoadPromise;
            let overlay = this.overlay;
            overlay.imageSize = await MapImageOverlayComponent.getImageSize(
                overlay.imageDataUrl
            );
            if (overlay.topLeft && overlay.pixelSize) {
                overlay.bounds = MapImageOverlayComponent.computeBounds(
                    overlay.topLeft,
                    overlay.pixelSize,
                    overlay.imageSize
                );
            }
            overlay.jpgName = this.imageFile.name;
            this.overlayEdited.emit(this.overlay);
        } catch (error) {
            this.errorMsg = "Failed uploading or assigning path overlay image.";
            console.error(error);
        } finally {
            this.bImageUploadProcessing = false;
        }
    }

    async onGPXFileInputChanged(event) {
        this.bRouteFileProcessing = true;
        if (!this.gpx) {
            this.gpx = new MapRouteGPXOverlay();
        }
        try {
            this.errorMsg = "";
            this.gpxFile = (event.target as HTMLInputElement).files[0];
            event.srcElement.value = null; // detect diffent file content but same filename on next edit
            var reader = new FileReader();
            const fileLoadPromise = new Promise<void>((completeFn, errorFn) => {
                reader.onload = async (event) => {
                    this.gpx.gpxDataUrl = reader.result as string;
                    completeFn();
                };
                reader.onerror = (error) => {
                    errorFn(error);
                };
            });
            reader.readAsDataURL(this.gpxFile);
            await fileLoadPromise;
            let gpx = this.gpx;
            gpx.gpxName = this.gpxFile.name;
            this.gpxEdited.emit(this.gpx);

        } catch (error) {
            this.errorMsg = "Failed uploading or assigning gpx image.";
            console.error(error);
        } finally {
            this.bRouteFileProcessing = false;
        }
    }

    async onSave() {
        this.bProcessing = true;

        try {
            if (this.imageFile) {
                let photoDoc = this.photoService.rxCollection.newDocument();
                this.photoService.assignWhoAndWhen(photoDoc);
                photoDoc.scoutPointId = this.getOwnerId();
                photoDoc.fileName = this.imageFileName;
                photoDoc.mimeType = this.imageFile.type;
                photoDoc.uploadDate = null;
                photoDoc.fileChecksum = Md5.hashStr(
                    this.overlay.imageDataUrl
                ) as string;
                photoDoc.title = "Scout Path Overlay";
                await photoDoc.save();
                this.overlay.imageId = photoDoc._id;
                await this.photoService.saveLocalPhotoFile(
                    photoDoc,
                    this.overlay.imageDataUrl
                );
            }

            let overlay = new MapImageOverlay();
            overlay.bounds = this.overlay.bounds;
            overlay.imageId = this.overlay.imageId;
            overlay.imageSize = this.overlay.imageSize;
            overlay.jpgName = this.overlay.jpgName;
            overlay.jgwName = this.overlay.jgwName;
            overlay.pixelSize = this.overlay.pixelSize;
            overlay.topLeft = this.overlay.topLeft;

            const oldImageId = await this.saveOverlay(overlay);
            await this.deleteImage(oldImageId);

            this.bEditing = false;

            this.onCancel();
        } catch (error) {
            this.photoService.publishError(error, this);
            return false;
        } finally {
            this.bProcessing = false;
        }
    }

    async onSaveGPXRoutes() {
        this.bProcessingforRoutesoverlay = true;

        try {
            if (this.gpxFile) {
                this.gpx.routeId = this.pathTestDoc.routeId;
                await this.pathTestDoc.atomicUpdate((doc) => {
                    doc.gpx = this.gpx
                    return doc;
                })
                this.gpxEdited.emit(this.gpx);

            }

            this.bEditingForRoutes = false;

            this.onCancelroutegpxfile(true);
        } catch (error) {
            console.log("error saving gpx routes")
            return false;
        } finally {
            this.bProcessingforRoutesoverlay = false;
        }
    }

    async deleteImage(imageId: string) {
        if (imageId) {
            const bSharedImage = this.getSharableOverlays().some(
                (existing) => existing.overlay?.imageId == imageId
            );

            if (bSharedImage) {
                console.log("onDelete() overlay image is shared (skipping)");
            } else {
                let image = await this.photoService.rxCollection
                    .findOne(imageId)
                    .exec();
                if (image) {
                    console.log(
                        "onDelete() removing photo image for deleted overlay. " +
                        image.fileName
                    );
                    image.remove();
                    //TODO - remove file on server???
                    //if ( !this.appState.isOffline() && image.uploadDate ) {
                    //  try {
                    //    await this.photoService.deleteRemotePhotoFile(image).toPromise()
                    //  } catch ( error ) {
                    //    this.photoService.publishError(error,this)
                    //  }
                    //}
                }
            }
        }
    }

    async deleteRouteFile(routeFileId: string) {
        await this.pathTestDoc.atomicUpdate((doc) => {
            doc.gpx = null;
            return doc;
        })
        this.gpxEdited.emit(null);
    }

    async onWorldDataFileInputChanged(event) {
        this.bWorldDataProcessing = true;
        try {
            this.errorMsg = "";
            var strRawData = "";
            const dataFile = (event.target as HTMLInputElement).files[0];
            event.srcElement.value = null; // detect diffent file content but same filename on next edit
            var reader = new FileReader();
            const fileLoadPromise = new Promise<void>((completeFn, errorFn) => {
                reader.onload = async (event) => {
                    strRawData = reader.result as string;
                    completeFn();
                };
                reader.onerror = (error) => {
                    errorFn(error);
                };
            });
            await reader.readAsText(dataFile);
            await fileLoadPromise;
            const worldDataLines = strRawData
                .split(/[\r\n]+/)
                .filter((line) => line.trim());
            console.log(JSON.stringify(worldDataLines));
            if (worldDataLines.length != 6) {
                this.errorMsg =
                    "Invalid world data file.  Expected six lines with numeric entries";
            } else {
                if (!this.overlay) {
                    this.overlay = new MapImageOverlay();
                }
                let overlay = this.overlay;
                overlay.pixelSize = new MapImageOverlayDimension(
                    Number(worldDataLines[0]),
                    Number(worldDataLines[3])
                );
                overlay.topLeft = new MapImageOverlayPoint(
                    Number(worldDataLines[4]),
                    Number(worldDataLines[5])
                );
                overlay.jgwName = dataFile.name;
                if (overlay.imageSize) {
                    overlay.bounds = MapImageOverlayComponent.computeBounds(
                        overlay.topLeft,
                        overlay.pixelSize,
                        overlay.imageSize
                    );
                    this.overlayEdited.emit(overlay);
                }
            }
        } catch (error) {
            this.errorMsg = "Failed loading/parsing JGW world data file contents";
            console.error(error);
        } finally {
            this.bWorldDataProcessing = false;
        }
    }

    toNumber(x: any) {
        return Number(x);
    }

    static computeBounds(
        worldTopLeft: MapImageOverlayPoint,
        worldPixelSize: MapImageOverlayDimension,
        imageSize: MapImageOverlayDimension
    ) {
        const worldTopLeft3857 = new L.Point(worldTopLeft.x, worldTopLeft.y, false);
        const worldBottomRight3857 = new L.Point(
            worldTopLeft3857.x + worldPixelSize.width * imageSize.width,
            worldTopLeft3857.y + worldPixelSize.height * imageSize.height
        );
        const topLeft4326 =
            L.Projection.SphericalMercator.unproject(worldTopLeft3857);
        const bottomRight4326 =
            L.Projection.SphericalMercator.unproject(worldBottomRight3857);
        let bounds = new MapImageOverlayBounds(
            new MapImageOverlayPoint(topLeft4326.lat, topLeft4326.lng),
            new MapImageOverlayPoint(bottomRight4326.lat, bottomRight4326.lng)
        );
        return bounds;
    }

    static async getImageSize(dataUrl: string) {
        const dim = await new Promise<MapImageOverlayDimension>(function (
            resolved,
            rejected
        ) {
            if (!dataUrl) {
                rejected("image url is null");
            } else {
                var i = new Image();
                i.onload = function () {
                    console.log("image loaded from url");
                    resolved(
                        new MapImageOverlayDimension(i.naturalWidth, i.naturalHeight)
                    );
                };
                i.onerror = function () {
                    rejected(
                        "Failed computing image size.  Could not create Image object for dataUrl"
                    );
                };
                i.src = dataUrl;
            }
        });
        return dim;
    }
}
